import { liquidGlassRowShadowSx, liquidGlassSurfaceFillSx } from '@focuslee/shared-mini-app';
import { Box, Collapse } from '@mui/material';
import { type FC, useState } from 'react';
import type { ListCategoryWithExternalDescription } from '../../client/types';
import { AchievementCategoryItem } from './AchievementCategoryItem';

type AchievementCategoriesViewProps = {
    categories: ListCategoryWithExternalDescription[];
    onCategoryClick: (category: ListCategoryWithExternalDescription) => void;
};

export const AchievementCategoriesView: FC<AchievementCategoriesViewProps> = ({ categories, onCategoryClick }) => {
    const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
    const toggleExpand = (id: string) => setExpandedMap(prev => ({ ...prev, [id]: !prev[id] }));

    // Group categories by parent-child relationship
    const childrenMap = categories.reduce<Record<string, ListCategoryWithExternalDescription[]>>((acc, c) => {
        const parentId = (c as any).parentId;
        if (parentId) {
            if (!acc[parentId]) acc[parentId] = [];
            acc[parentId].push(c);
        }
        return acc;
    }, {});

    const rootCategories = categories.filter(category => !(category as any).parentId);
    return (
        <Box
            style={{
                paddingRight: 16,
                paddingLeft: 16,
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
            }}
        >
            {rootCategories.map(category => {
                const subCategories = childrenMap[category.id] || [];
                const hasSubCategories = subCategories.length > 0;
                const expanded = !!expandedMap[category.id];

                return (
                    <Box key={category.id} sx={{ width: '100%', borderRadius: '16px', overflow: 'hidden', ...liquidGlassRowShadowSx }}>
                        <Box sx={{ borderRadius: 'inherit', ...liquidGlassSurfaceFillSx }}>
                            <AchievementCategoryItem
                                category={category}
                                childrenMap={childrenMap}
                                expandedMap={expandedMap}
                                externalDescription={category.externalDescription}
                                onCategoryClick={() => onCategoryClick(category)}
                                onToggleExpand={toggleExpand}
                            />
                            {hasSubCategories && (
                                <Collapse in={expanded} timeout="auto" unmountOnExit>
                                    {subCategories.map(subCategory => (
                                        <AchievementCategoryItem
                                            key={subCategory.id}
                                            category={subCategory}
                                            childrenMap={childrenMap}
                                            expandedMap={expandedMap}
                                            isSubCategory={true}
                                            showDivider
                                            externalDescription={subCategory.externalDescription}
                                            onCategoryClick={() => onCategoryClick(subCategory)}
                                            onToggleExpand={toggleExpand}
                                        />
                                    ))}
                                </Collapse>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};
